import { getServiceAnalyticUsages } from '@/modules/Service/services'
import {
    MonitoringQueries,
    ServiceMonitoringProps,
} from '@/modules/Service/types'
import { formatDateWithFormatter } from '@/utils/function'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { Loading } from 'vnetwork-ui'
import Header from '../../../shared/Header'
import AnalyticSeriesChart, {
    AnalyticSeriesData,
} from '../../../shared/AnalyticSeriesChart'
import DurationChart from './DurationChart'
import JobsChart from './JobsChart'
import VMSDurationChart from './VMSDurationChart'
import VMSUsageChart from './VMSUsageChart'

const today = dayjs()

// All four transcode metrics fetched in parallel and stacked vertically (no
// tabs/toggles) — mirrors the CDN Monitoring Promise.all + stacked-section layout.
const Monitoring: React.FC<ServiceMonitoringProps> = ({ service }) => {
    const [analyticQueries, setAnalyticQueries] = useState<MonitoringQueries>({
        range: 'last-month',
        gte: today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        lte: today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
    })

    const buildParams = (type: string) => ({
        gte: formatDateWithFormatter(analyticQueries?.gte, 'YYYY-MM-DD'),
        lte: formatDateWithFormatter(analyticQueries?.lte, 'YYYY-MM-DD'),
        type,
    })

    const { data, renderState, isPending, isLoading, isFetching, isSuccess } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.SERVICES,
                'analytics',
                service?.service_creatable_uuid,
                'transcoding',
                analyticQueries?.gte,
                analyticQueries?.lte,
            ],
            queryFn: () =>
                Promise.all([
                    getServiceAnalyticUsages({
                        serviceID: service?.service_id,
                        params: buildParams('/lms_transcoding_duration'),
                    }),
                    getServiceAnalyticUsages({
                        serviceID: service?.service_id,
                        params: buildParams('/no_of_live_jobs'),
                    }),
                    getServiceAnalyticUsages({
                        serviceID: service?.service_id,
                        params: buildParams('/vms_transcoding_duration'),
                    }),
                    getServiceAnalyticUsages({
                        serviceID: service?.service_id,
                        params: buildParams('/vms_transcoding_usage'),
                    }),
                    getServiceAnalyticUsages({
                        serviceID: service?.service_id,
                        params: buildParams('/lms_transmuxing_duration'),
                    }),
                    getServiceAnalyticUsages({
                        serviceID: service?.service_id,
                        params: buildParams('/vms_transmuxing_duration'),
                    }),
                ]),
        })

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const isReady = isSuccess && Array.isArray(data)
    const [
        lmsDuration,
        lmsJobs,
        vmsDuration,
        vmsUsage,
        lmsTransmux,
        vmsTransmux,
    ] = (
        isReady
            ? data
            : [undefined, undefined, undefined, undefined, undefined, undefined]
    ) as (AnalyticSeriesData | undefined)[]

    return (
        <div className="space-y-2">
            <Header
                analyticQueries={analyticQueries}
                setAnalyticQueries={setAnalyticQueries}
            />

            {isPending || isLoading || isFetching ? (
                <Loading.FadingDots width={40} height={40} />
            ) : (
                <div className="space-y-2">
                    <DurationChart data={lmsDuration} />
                    <AnalyticSeriesChart
                        data={lmsTransmux}
                        title="Live Transmux Duration"
                        unit="hours"
                        color="#0891b2"
                        variant="bar"
                        totalLabel="Total Hours"
                        emptyText="No transmux data available for the selected period"
                    />
                    <JobsChart data={lmsJobs} />
                    <VMSDurationChart data={vmsDuration} />
                    <AnalyticSeriesChart
                        data={vmsTransmux}
                        title="VOD Transmux Duration"
                        unit="hours"
                        color="#db2777"
                        variant="bar"
                        totalLabel="Total Hours"
                        emptyText="No transmux data available for the selected period"
                    />
                    <VMSUsageChart data={vmsUsage} />
                </div>
            )}
        </div>
    )
}

export default Monitoring
